"use client";

import { c as _c } from "react/compiler-runtime";
import React, { createContext, startTransition, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { APP_NAME_DOMAIN } from "@features/application/app-consts";
import { SidePanelWrapper } from "./side-panel-wrapper";
import { PanelConfig, SidePanelContextValue, SidePanelProviderProps } from "./types";

/**
 * Base z-index for panels (ensures panels appear above standard page content)
 */
const BASE_Z_INDEX = 10;

/**
 * Z-index increment between panel layers
 */
const Z_INDEX_INCREMENT = 2;

/**
 * Context for side panel state management
 */
const SidePanelContext = createContext<SidePanelContextValue | null>(null);

/**
 * Hook to access side panel context
 * @throws Error if used outside SidePanelProvider
 */
export const useSidePanel = () => {
  const context = useContext(SidePanelContext);
  if (!context) {
    throw new Error("useSidePanel must be used within SidePanelProvider");
  }
  return context;
};

/**
 * Provider component for side panel state management
 * Manages multiple simultaneous panels with z-index stacking
 */
export const SidePanelProvider: React.FC<SidePanelProviderProps> = t0 => {
  const $ = _c(29);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Map();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [activePanels, setActivePanels] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [panelOrder, setPanelOrder] = useState(t2);
  const recalculateZIndexes = _temp;
  let t3;
  let t4;
  if ($[2] !== panelOrder) {
    t3 = () => {
      if (panelOrder.length === 0) {
        return;
      }
      startTransition(() => {
        setActivePanels(prevPanels => recalculateZIndexes(panelOrder, prevPanels));
      });
    };
    t4 = [panelOrder, recalculateZIndexes];
    $[2] = panelOrder;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = (id_0, config) => {
      setPanelOrder(prevOrder => {
        const existingIndex = prevOrder.indexOf(id_0);
        if (existingIndex !== -1 && existingIndex === prevOrder.length - 1) {
          return prevOrder;
        }
        return existingIndex !== -1 ? [...prevOrder.filter(panelId => panelId !== id_0), id_0] : [...prevOrder, id_0];
      });
      setActivePanels(prevPanels_0 => {
        const newPanels = new Map(prevPanels_0);
        if (!newPanels.has(id_0)) {
          newPanels.set(id_0, {
            ...config,
            id: id_0,
            zIndex: BASE_Z_INDEX,
            isTop: false,
            index: prevPanels_0.size + 1
          });
        }
        return newPanels;
      });
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const openPanel = t5;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = (id_1, config_0) => {
      setActivePanels(prevPanels_1 => {
        const panel_0 = prevPanels_1.get(id_1);
        if (!panel_0) {
          return prevPanels_1;
        }
        const nextPanels = new Map(prevPanels_1);
        nextPanels.set(id_1, {
          ...panel_0,
          ...config_0
        });
        return nextPanels;
      });
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const updatePanel = t6;
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = id_2 => {
      setPanelOrder(prevOrder_0 => prevOrder_0.filter(panelId_0 => panelId_0 !== id_2));
      setActivePanels(prevPanels_2 => {
        const newPanels_0 = new Map(prevPanels_2);
        newPanels_0.delete(id_2);
        return newPanels_0;
      });
    };
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const closePanel = t7;
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setPanelOrder([]);
      setActivePanels(new Map());
    };
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const closeAllPanels = t8;
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = id_3 => {
      setPanelOrder(prevOrder_1 => {
        if (!prevOrder_1.includes(id_3)) {
          console.warn(`[${APP_NAME_DOMAIN}] Cannot switch to panel "${id_3}": panel not found`);
          return prevOrder_1;
        }
        const currentIndex = prevOrder_1.indexOf(id_3);
        if (currentIndex === prevOrder_1.length - 1) {
          return prevOrder_1;
        }
        return [...prevOrder_1.filter(panelId_1 => panelId_1 !== id_3), id_3];
      });
    };
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const switchToPanel = t9;
  let t10;
  if ($[10] !== activePanels) {
    t10 = id_4 => activePanels.has(id_4);
    $[10] = activePanels;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  const isPanelOpen = t10;
  const topPanelId = panelOrder.length > 0 ? panelOrder[panelOrder.length - 1] : null;
  let t11;
  if ($[12] !== activePanels || $[13] !== isPanelOpen || $[14] !== panelOrder || $[15] !== topPanelId) {
    t11 = {
      activePanels,
      panelOrder,
      topPanelId,
      openPanel,
      updatePanel,
      closePanel,
      closeAllPanels,
      switchToPanel,
      isPanelOpen
    };
    $[12] = activePanels;
    $[13] = isPanelOpen;
    $[14] = panelOrder;
    $[15] = topPanelId;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const contextValue = t11;
  let t12;
  if ($[17] !== activePanels || $[18] !== panelOrder) {
    let t13;
    if ($[20] !== activePanels) {
      t13 = id_5 => activePanels.get(id_5);
      $[20] = activePanels;
      $[21] = t13;
    } else {
      t13 = $[21];
    }
    t12 = panelOrder.map(t13).filter(Boolean);
    $[17] = activePanels;
    $[18] = panelOrder;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const sortedPanels = t12 as PanelConfig[];
  let t13;
  if ($[22] !== sortedPanels) {
    let t14;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = (panel_1, i) => <SidePanelWrapper key={panel_1.id} id={panel_1.id} isOpen={true} isTop={panel_1.isTop} zIndex={panel_1.zIndex} title={panel_1.title} description={panel_1.description} size={panel_1.size} onClose={() => closePanel(panel_1.id)} overlayClassName={i > 0 ? "opacity-0" : undefined}>{panel_1.children}</SidePanelWrapper>;
      $[24] = t14;
    } else {
      t14 = $[24];
    }
    t13 = sortedPanels.map(t14);
    $[22] = sortedPanels;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[25] !== children || $[26] !== contextValue || $[27] !== t13) {
    t14 = <SidePanelContext.Provider value={contextValue}>{children}{t13}</SidePanelContext.Provider>;
    $[25] = children;
    $[26] = contextValue;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  return t14;
};
function _temp(order, panels) {
  const updatedPanels = new Map(panels);
  const topId = order.length > 0 ? order[order.length - 1] : null;
  order.forEach((id, index) => {
    const panel = updatedPanels.get(id);
    if (panel) {
      updatedPanels.set(id, {
        ...panel,
        zIndex: BASE_Z_INDEX + index * Z_INDEX_INCREMENT,
        isTop: id === topId
      });
    }
  });
  return updatedPanels;
}